/**
 * Personal (per-user) sync for the Documents (notes) and Task Manager (boards)
 * extensions. Their records are personal, not workspace-bound: they live in a
 * fixed local scope so they read the same no matter which API workspace is
 * active, and sync across the user's own devices via the user-scoped
 * /sync/personal/* endpoints (the server derives the user from the auth token).
 *
 * Local-first: signed out, records write to `personal:local` (device-only, no
 * engine task). On sign-in they move into `personal:<userId>` and the engine
 * task pushes them up + pulls other devices' changes (LWW). Keying the
 * signed-in bucket per-user avoids leaking one account's notes to another on a
 * shared device.
 *
 * WHICH scope that is — `personalScopeId()` — lives in `./personalScope`, a
 * separate invoke-free module, so that asking for it does not import the engine
 * controls below (17.09.2026; see that file's header).
 */
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { bumpDocumentsTree } from '../extensions/bundled/documents/documentsUiStore';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';


/**
 * Idempotent setup: fold any signed-out drafts into the user's scope and start
 * the personal engine task (push offline work up, pull other devices' changes).
 * No-op signed out or outside the desktop app. Called on auth settle.
 */
export async function ensurePersonalSync() {
  if (!isTauri()) return;
  const { isAuthenticated, user } = useAuthStore.getState();
  if (!isAuthenticated || user?.id == null) return;
  const scope = `personal:${user.id}`;
  try {
    // Move signed-out drafts (rows + pending outbox) into this user's scope.
    // ALWAYS, whatever the sync choice: the app reads `personal:<id>` for a
    // signed-in user, so drafts left in `personal:local` would vanish from
    // view. Without the engine task below they simply stay on this device.
    const moved = await invoke('personal_migrate_local_scope', { toScope: scope });
    // No switch any more (card C9): the personal engine always runs, and what
    // it carries is decided per item. A "this device" item lives in
    // `device:<id>`, which the engine never queues — see
    // src-tauri/src/sync/item_location.rs. A Free account over its cloud limit
    // therefore stops SYNCING NEW ITEMS without anything being turned off.
    // Idempotent; the first cycle pulls the change log from cursor 0
    // (creates and tombstones) non-destructively.
    await invoke('sync_register_personal', { scopeId: scope });
    if (moved > 0) {
      bumpDocumentsTree();
      bumpBoards();
    }
  } catch (err) {
    console.warn('[personal-sync] setup failed:', err);
  }
}

/**
 * Stop the personal engine task (sync turned off). Records stay on this device
 * and on the server; nothing is deleted.
 */
export async function stopPersonalSync() {
  if (!isTauri()) return;
  const { user } = useAuthStore.getState();
  if (user?.id == null) return;
  try {
    await invoke('sync_unregister_workspace', { workspaceId: `personal:${user.id}` });
  } catch (err) {
    console.warn('[personal-sync] stop failed:', err);
  }
}


